"use client";

import { useEffect, useRef } from "react";

interface SyncPlayingTrackOptions {
  playing: string | null;
  /** `null` means "strip from URL"; callers wire this to a navigate-based setter that maps `null -> undefined` in the search patch. */
  setPlaying: (value: string | null) => void;
  currentYoutubeId?: string;
  /** Called when playback starts and the param was previously unset (e.g. set tab to "tracks"). */
  onPlaybackStart?: () => void;
}

/**
 * Keeps the `playing` URL search param in sync with the current player track.
 * Caller owns the setter (TanStack Router navigate) so this hook stays
 * decoupled from the router — no re-render loops.
 */
export function useSyncPlayingTrackSearchParam({
  playing,
  setPlaying,
  currentYoutubeId,
  onPlaybackStart,
}: SyncPlayingTrackOptions) {
  const onPlaybackStartRef = useRef(onPlaybackStart);
  onPlaybackStartRef.current = onPlaybackStart;

  // Use a ref for `playing` so the effect only fires when the *player*
  // changes track, not when the URL changes (e.g. back navigation).
  // Without this, pressing back removes `?playing` from the URL, but the
  // effect immediately re-pushes it because the player is still active.
  const playingRef = useRef(playing);
  playingRef.current = playing;

  // Track whether the player has ever been active so we don't clear
  // the URL param before autoplay gets a chance to run.
  const hasEverPlayed = useRef(false);

  useEffect(() => {
    if (currentYoutubeId) {
      hasEverPlayed.current = true;
    }

    if (!hasEverPlayed.current) return;

    const desired = currentYoutubeId ?? null;
    if (playingRef.current === desired) return;

    setPlaying(desired);
    if (desired && !playingRef.current) {
      onPlaybackStartRef.current?.();
    }
  }, [currentYoutubeId, setPlaying]);
}

interface PlaylistTrack {
  youtubeId: string;
}

interface AutoPlayFromSearchParamOptions {
  playing: string | null;
  playlist: PlaylistTrack[];
  onPlayByYoutubeId: (youtubeId: string) => void;
  onAutoPlay?: () => void;
}

/**
 * Auto-plays the track from the `playing` URL param once, when playlist is available.
 */
export function useAutoPlayFromSearchParam({
  playing,
  playlist,
  onPlayByYoutubeId,
  onAutoPlay,
}: AutoPlayFromSearchParamOptions) {
  const hasAutoPlayed = useRef(false);
  const onPlayRef = useRef(onPlayByYoutubeId);
  const onAutoPlayRef = useRef(onAutoPlay);
  onPlayRef.current = onPlayByYoutubeId;
  onAutoPlayRef.current = onAutoPlay;

  useEffect(() => {
    if (playlist.length === 0 || hasAutoPlayed.current) return;
    if (!playing) return;

    hasAutoPlayed.current = true;
    const playlistHasTrack = playlist.some((track) => track.youtubeId === playing);
    if (!playlistHasTrack) return;

    onPlayRef.current(playing);
    onAutoPlayRef.current?.();
  }, [playlist, playing]);
}
